import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import type { ShotFeed, Shot } from '../stores/types/shots';
import type { MediaItem } from '../stores/types/media';
import { baseShot, FEED_TYPES } from '../stores/reducers/shots';
import { GreenroomState as GS } from '../stores/types/types';
import { LOCATION_CANVAS_ROUTES } from '../stores/reducers/video';
import { getOnsiteLayout, isOneUpLayout } from '../utils/video-layout';
import { FeatureFlagsEnum } from '../types/blp-feature-flags';
import { selectFeatureFlag } from '../stores/selectors/blp-feature-flags';
import { STREAM_STATES, BroadcastStatus } from '../stores/types/broadcasting';
import useBroadcastStatus from './use-broadcast-status';

const useCanvasConfig = (inputShot: Shot | undefined = baseShot) => {
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const limitedInput = useSelector((s: GS) => s.SessionReducer.limitedInput);
	const limitedInputSmartCanvas = useSelector((s: GS) => s.SessionReducer.limitedInputSmartCanvas);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const streamState = useSelector((s: GS) => s.BroadcastReducer.streamState);
	const syncedContentWindow = useSelector(selectFeatureFlag(FeatureFlagsEnum.synced_content_window));
	const broadcastStatus = useBroadcastStatus();

	// The broadcast is live when the stream has reached its MAIN phase (persisted,
	// so an embed that connects mid-stream still sees it via REST before any
	// socket event arrives), or when broadcastStatus is active — the same
	// provider-agnostic check useBroadcastState exposes (bullman's serverState-driven
	// path and LiveKit/spielberg's path both funnel into this). Uses the standalone
	// use-broadcast-status hook rather than useBroadcastState itself: that hook pulls
	// in broadcasting/constants.ts, sockets, and analytics, which creates a circular
	// import back through this file (use-canvas-config is imported deep in the
	// shot/media tree those dependencies also reach) and crashes module init with
	// "timeToMilliseconds is not a function".
	// NOTE: do not use VideoReducer.isLive here — SET_LIVE is never dispatched, so
	// that flag is always false.
	const isBroadcastLive = streamState === STREAM_STATES.MAIN
		|| broadcastStatus === BroadcastStatus.active;

	const shouldHideSlidesFromBroadcast = useMemo(
		() => !!syncedContentWindow && isBroadcastLive && embed,
		[syncedContentWindow, isBroadcastLive, embed],
	);

	// A media feed is a slide deck (ppt/pdf/keynote) when its media item has more
	// than one page; a single image or single video is not a deck.
	const isSlideDeckFeed = (feed: ShotFeed) => {
		if (feed.feedType !== FEED_TYPES.MEDIA || feed.media == null) {
			return false;
		}
		const mediaItem = media.find((m: MediaItem) => m.id === feed.media);
		return (mediaItem?.media_data?.image_array?.length ?? 0) > 1;
	};

	const hideSelf = useMemo(
		() => (
			limitedInput
				? limitedInputSmartCanvas
				: onsiteLocations.find((l) => l.id === onsiteLocationSession?.id)?.hideSelfView ?? false
		),
		[
			onsiteLocationSession,
			onsiteLocations,
			limitedInput,
			limitedInputSmartCanvas,
		],
	);

	// only use remove onsite feed from canvas if both are true:
	// 1) on the onsite location session canvas view
	// 2) "Smart Canvas" setting is enabled for the current location
	const shouldGetOnsiteCanvasConfig = useMemo(
		() => !!onsiteLocationSession
			&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession?.route)
			&& hideSelf,
		[onsiteLocationSession, hideSelf],
	);

	const getCanvasFeedsAndLayout = (shot: Shot) => {
		const {
			shot_data: {
				shotFeeds,
				forcedLayout,
			},
		} = shot;

		let feeds = shotFeeds;
		let layout = forcedLayout;

		// remove onsite feed from shot feeds if on canvas
		// NOTE: handling one up layouts will be addressed in a separate ticket
		if (shouldGetOnsiteCanvasConfig && !isOneUpLayout(forcedLayout)) {
			const filteredFeeds = feeds
				.filter((f) => (
					f.audioOnly
					|| (onsiteLocationSession?.id && f.onsiteLocationId !== onsiteLocationSession?.id)
					|| (!onsiteLocationSession?.id && f.presenter !== user?.gr_user)
				))
				.map((f, i) => ({ ...f, position: i }));

			// adjust layout for each removed feed
			const numRemoved = feeds.length - filteredFeeds.length;
			for (let i = 0; i < numRemoved; i += 1) {
				layout = getOnsiteLayout(layout);
			}

			feeds = filteredFeeds;
		}

		// remove presented slide decks from the broadcast canvas when they are
		// relayed to the synced content window. If the only feed is the slide the
		// canvas is intentionally left blank.
		if (shouldHideSlidesFromBroadcast) {
			const withoutSlides = feeds
				.filter((f) => !isSlideDeckFeed(f))
				.map((f, i) => ({ ...f, position: i }));

			// adjust layout for each removed feed
			const numRemoved = feeds.length - withoutSlides.length;
			for (let i = 0; i < numRemoved; i += 1) {
				layout = getOnsiteLayout(layout);
			}

			feeds = withoutSlides;
		}

		return { shotFeeds: feeds, forcedLayout: layout };
	};

	const { shotFeeds, forcedLayout } = useMemo(
		() => getCanvasFeedsAndLayout(inputShot),
		[
			inputShot.shot_data.shotFeeds,
			inputShot.shot_data.forcedLayout,
			shouldGetOnsiteCanvasConfig,
			shouldHideSlidesFromBroadcast,
			media,
			onsiteLocationSession,
			user,
		],
	);

	const showingHoldSlide = useMemo(
		() => (
			shouldGetOnsiteCanvasConfig // location canvas view with smart canvas enabled
			&& isOneUpLayout(forcedLayout) // shot feed is a one up
			&& shotFeeds.some((feed) => ( // on canvas shot feed is the onsite user
				!feed.audioOnly
				&& ((onsiteLocationSession?.id && feed.onsiteLocationId === onsiteLocationSession?.id)
				|| (!onsiteLocationSession?.id && feed.presenter === user?.gr_user))
			))
		),
		[
			shouldGetOnsiteCanvasConfig,
			forcedLayout,
			shotFeeds,
			onsiteLocationSession,
			user,
		],
	);

	return {
		shotFeeds,
		forcedLayout,
		showingHoldSlide,
		shouldGetOnsiteCanvasConfig,
	};
};

export default useCanvasConfig;
